/**
 * 门店营收监控：按「一个门店一个 JSON」将浏览器中的数据持久化到 localStorage
 * Key: ms_store_revenue_store_${storeId}
 *
 * 数据结构（v1）：
 * - sources: 共享的源数据（多组件共用同一接口返回）
 * - components: 每个组件独立分支（自有数据源，单组件使用）
 *
 * 向后兼容：仍支持读取旧版扁平结构，写入时自动迁移到新结构
 *
 * 部署后查看 localStorage：Local Storage 按「当前页面的域名」隔离。
 * 在本地浏览器打开部署地址（如 https://xxx.com）时，需在 DevTools → Application → Local Storage
 * 中选中该域名（如 https://xxx.com），才能看到 ms_store_revenue_store_* 等 key；
 * 若选错域名（如 localhost 或 file://）则看不到部署站点的缓存。
 */

const PREFIX = 'ms_store_revenue_store_';
const STORAGE_VERSION = 1;

function storageKey(storeId: number | string): string {
  return `${PREFIX}${String(storeId)}`;
}

export interface StoreSlicePayload {
  data: unknown;
  fetchedAt: number;
}

export interface OperationAnalysisPayload extends StoreSlicePayload {
  granularity: string;
}

/** 共享源数据：多组件共用同一接口返回 */
export interface StoreSources {
  /** store-data-from-cache 接口返回，供订单流速、渠道分析/信息、房态健康、客源、折扣等 */
  storeDataFromCache?: StoreSlicePayload;
  /** 运营分析（按粒度：日度7天等），供渠道分析、出租率等组件 */
  operationAnalysis?: Record<string, StoreSlicePayload>;
  /** businessReportList 日度原始 list，前端直调 PMS 后缓存，供房态健康、客源、折扣等加工 */
  businessReportDaily?: StoreSlicePayload;
  /** FSM 远期房态 futureRoomStatusList，供远期房态展示已占用房量、可售房量 */
  futureRoomStatusList?: StoreSlicePayload;
}

/**
 * 组件分支：每组件对应「用于展示」的独立数据结构，便于检查数据是否完整正确。
 * - 前 4 个由各自独立接口写入；其余由 storeDataFromCache 写入时同步写入展示结构。
 */
export interface StoreComponents {
  /** 自有数据源 */
  'channel-price'?: StoreSlicePayload;
  'room-status'?: StoreSlicePayload;
  'room-status-recently'?: StoreSlicePayload;
  revpar?: StoreSlicePayload;
  /** 以下由 storeDataFromCache 解析出的展示结构 */
  'order-traffic'?: StoreSlicePayload;
  'room-health'?: StoreSlicePayload;
  'customer-source'?: StoreSlicePayload;
  'discount-analysis'?: StoreSlicePayload;
  'channel-analysis'?: StoreSlicePayload;
  'channel-info'?: StoreSlicePayload;
  'channel-info-chart'?: StoreSlicePayload;
  'occupancy-analysis'?: StoreSlicePayload;
  'operation-efficiency'?: StoreSlicePayload;
  /** 来自缓存的 RevPAR（若与 PMS 直调 revpar 不同可对比） */
  'revpar-from-cache'?: StoreSlicePayload;
}

export interface StoreRevenueLocalStorageData {
  version?: number;
  sources?: StoreSources;
  components?: StoreComponents;
}

/** 对外读取视图：兼容旧版 flat 结构，供现有 callers 无改动使用 */
export interface StoreRevenueLocalStorageView {
  channelPrice?: StoreSlicePayload;
  roomStatus?: StoreSlicePayload;
  roomStatusRecently?: StoreSlicePayload;
  revpar?: StoreSlicePayload;
  storeDataFromCache?: StoreSlicePayload;
  operationAnalysis?: Record<string, StoreSlicePayload>;
  businessReportDaily?: StoreSlicePayload;
  futureRoomStatusList?: StoreSlicePayload;
}

/** 旧 slice 名 -> 新结构路径 */
const SLICE_TO_SOURCE: Record<string, keyof StoreSources> = {
  storeDataFromCache: 'storeDataFromCache',
  operationAnalysis: 'operationAnalysis',
  businessReportDaily: 'businessReportDaily',
  futureRoomStatusList: 'futureRoomStatusList',
};

const SLICE_TO_COMPONENT: Record<string, keyof StoreComponents> = {
  channelPrice: 'channel-price',
  roomStatus: 'room-status',
  roomStatusRecently: 'room-status-recently',
  revpar: 'revpar',
};

function isNewStructure(raw: unknown): raw is StoreRevenueLocalStorageData {
  if (raw == null || typeof raw !== 'object') return false;
  const o = raw as Record<string, unknown>;
  return o.version === STORAGE_VERSION && ('sources' in o || 'components' in o);
}

/** 从新结构生成兼容视图 */
function toView(data: StoreRevenueLocalStorageData): StoreRevenueLocalStorageView {
  const sources = data.sources ?? {};
  const components = data.components ?? {};
  return {
    channelPrice: components['channel-price'],
    roomStatus: components['room-status'],
    roomStatusRecently: components['room-status-recently'],
    revpar: components.revpar,
    storeDataFromCache: sources.storeDataFromCache,
    operationAnalysis: sources.operationAnalysis,
    businessReportDaily: sources.businessReportDaily,
    futureRoomStatusList: sources.futureRoomStatusList,
  };
}

/** 从旧版扁平结构迁移到新结构 */
function migrateFromLegacy(legacy: Record<string, unknown>): StoreRevenueLocalStorageData {
  const sources: StoreSources = {};
  const components: StoreComponents = {};
  if (legacy.storeDataFromCache != null && typeof legacy.storeDataFromCache === 'object') {
    sources.storeDataFromCache = legacy.storeDataFromCache as StoreSlicePayload;
  }
  if (legacy.operationAnalysis != null && typeof legacy.operationAnalysis === 'object') {
    sources.operationAnalysis = legacy.operationAnalysis as Record<string, StoreSlicePayload>;
  }
  if (legacy.channelPrice != null && typeof legacy.channelPrice === 'object') {
    components['channel-price'] = legacy.channelPrice as StoreSlicePayload;
  }
  if (legacy.roomStatus != null && typeof legacy.roomStatus === 'object') {
    components['room-status'] = legacy.roomStatus as StoreSlicePayload;
  }
  if (legacy.roomStatusRecently != null && typeof legacy.roomStatusRecently === 'object') {
    components['room-status-recently'] = legacy.roomStatusRecently as StoreSlicePayload;
  }
  if (legacy.revpar != null && typeof legacy.revpar === 'object') {
    components.revpar = legacy.revpar as StoreSlicePayload;
  }
  if (legacy.futureRoomStatusList != null && typeof legacy.futureRoomStatusList === 'object') {
    sources.futureRoomStatusList = legacy.futureRoomStatusList as StoreSlicePayload;
  }
  return { version: STORAGE_VERSION, sources, components };
}

/**
 * 读取某门店在 localStorage 中的完整 JSON
 * 返回兼容视图：channelPrice、roomStatus 等与旧版一致，支持新旧两种存储格式
 */
export function getStoreFromLocalStorage(storeId: number | string): StoreRevenueLocalStorageView | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(storageKey(storeId));
    if (raw == null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    if (isNewStructure(obj)) {
      return toView(obj);
    }
    return parsed as StoreRevenueLocalStorageView;
  } catch {
    return null;
  }
}

/** slice 类型：兼容旧调用方传入的 key */
export type StoreSliceKey =
  | 'channelPrice'
  | 'roomStatus'
  | 'roomStatusRecently'
  | 'revpar'
  | 'storeDataFromCache'
  | 'operationAnalysis'
  | 'futureRoomStatusList';

/** store-data-from-cache 接口返回的 data 中，各字段与组件展示分支的对应关系 */
const STORE_DATA_SLICE_TO_COMPONENT: Record<string, keyof StoreComponents> = {
  orderTraffic: 'order-traffic',
  roomStatusRecently: 'room-status-recently',
  roomHealth: 'room-health',
  customerSource: 'customer-source',
  discountAnalysis: 'discount-analysis',
  channelAnalysis: 'channel-analysis',
  channelInfo: 'channel-info',
  channelInfoChart: 'channel-info-chart',
  occupancyAnalysis: 'occupancy-analysis',
  operationEfficiency: 'operation-efficiency',
  revPARChart: 'revpar-from-cache',
};

/**
 * 写入 store-data-from-cache：同时写入 sources（共享源数据）与 components 下各组件展示结构，便于核对完整性与正确性。
 */
export function setStoreDataFromCacheWithComponentSlices(
  storeId: number | string,
  data: Record<string, unknown>,
  fetchedAt: number
): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = storageKey(storeId);
    const currentRaw = localStorage.getItem(key);
    const defaultCurrent: StoreRevenueLocalStorageData = {
      version: STORAGE_VERSION,
      sources: {},
      components: {},
    };
    let current: StoreRevenueLocalStorageData = defaultCurrent;
    if (currentRaw) {
      try {
        const parsed = JSON.parse(currentRaw) as unknown;
        if (isNewStructure(parsed)) {
          current = parsed;
        } else if (parsed != null && typeof parsed === 'object') {
          current = migrateFromLegacy(parsed as Record<string, unknown>);
        }
      } catch {
        // 解析失败时使用默认
      }
    }

    const sources: StoreSources = { ...current.sources };
    const components: StoreComponents = { ...current.components };

    sources.storeDataFromCache = { data, fetchedAt };
    for (const [apiKey, componentKey] of Object.entries(STORE_DATA_SLICE_TO_COMPONENT)) {
      const slice = data[apiKey];
      if (slice !== undefined && slice !== null) {
        components[componentKey] = { data: slice, fetchedAt };
      }
    }

    const next: StoreRevenueLocalStorageData = {
      version: STORAGE_VERSION,
      sources,
      components,
    };
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // 忽略持久化失败
  }
}

/**
 * 写入某门店下的一块数据（合并到该门店的 JSON 中）
 * - storeDataFromCache / operationAnalysis -> sources（共享源数据）
 * - channelPrice / roomStatus / roomStatusRecently / revpar -> components（每组件独立分支）
 */
export function setStoreSliceInLocalStorage(
  storeId: number | string,
  slice: StoreSliceKey,
  payload: StoreSlicePayload | OperationAnalysisPayload
): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = storageKey(storeId);
    const currentRaw = localStorage.getItem(key);
    const defaultCurrent: StoreRevenueLocalStorageData = {
      version: STORAGE_VERSION,
      sources: {},
      components: {},
    };
    let current: StoreRevenueLocalStorageData = defaultCurrent;
    if (currentRaw) {
      try {
        const parsed = JSON.parse(currentRaw) as unknown;
        if (isNewStructure(parsed)) {
          current = parsed;
        } else if (parsed != null && typeof parsed === 'object') {
          current = migrateFromLegacy(parsed as Record<string, unknown>);
        }
      } catch {
        // 解析失败时使用默认
      }
    }

    const sources: StoreSources = { ...current.sources };
    const components: StoreComponents = { ...current.components };

    if (slice === 'operationAnalysis' && 'granularity' in payload) {
      const gran = payload.granularity;
      const nextOp = { ...sources.operationAnalysis };
      nextOp[gran] = { data: payload.data, fetchedAt: payload.fetchedAt };
      sources.operationAnalysis = nextOp;
    } else if (SLICE_TO_SOURCE[slice]) {
      const k = SLICE_TO_SOURCE[slice];
      sources[k] = { data: payload.data, fetchedAt: payload.fetchedAt };
    } else if (SLICE_TO_COMPONENT[slice]) {
      const k = SLICE_TO_COMPONENT[slice];
      components[k] = { data: payload.data, fetchedAt: payload.fetchedAt };
    }

    const next: StoreRevenueLocalStorageData = {
      version: STORAGE_VERSION,
      sources,
      components,
    };
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // 忽略持久化失败
  }
}

/**
 * store-data-from-cache 的有效期（5 分钟），用于判断是否可先用本地缓存再后台刷新
 */
export const STORE_DATA_FROM_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * 从 localStorage 读取 storeDataFromCache 切片，若存在且在 TTL 内则返回 { data, fetchedAt }
 * 用于「先展示缓存、再按需后台刷新」的加载策略
 */
export function getStoreDataFromCacheFromLocalStorage(
  storeId: number | string
): { data: Record<string, unknown>; fetchedAt: number } | null {
  const view = getStoreFromLocalStorage(storeId);
  const slice = view?.storeDataFromCache;
  if (!slice?.data || typeof slice.fetchedAt !== 'number') return null;
  if (Date.now() - slice.fetchedAt > STORE_DATA_FROM_CACHE_TTL_MS) return null;
  if (typeof slice.data !== 'object' || slice.data === null) return null;
  return { data: slice.data as Record<string, unknown>, fetchedAt: slice.fetchedAt };
}

/**
 * 清除某门店在 localStorage 中的整条缓存（key: ms_store_revenue_store_${storeId}）。
 * 用于调试或「强制完全重新拉数」；刷新时仅清内存由 storeRevenueCacheClear 负责，不默认删 localStorage。
 */
export function clearStoreFromLocalStorage(storeId: number | string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(storageKey(storeId));
  } catch {
    // 忽略
  }
}
